import { useEffect, useState } from "react";
import { ArrowLeft, FileDown, Mail, Save, Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 });
const FREQ_OPTIONS = [
  { value: "weekly", label: "Semanal" },
  { value: "biweekly", label: "Quincenal" },
  { value: "monthly", label: "Mensual" },
  { value: "hourly", label: "Por hora" },
  { value: "commission", label: "Comisión" },
  { value: "mixed", label: "Mixto" },
];

const kpiStyle = (bg = "#f9fafb") => ({
  background: bg, padding: "18px 20px", borderRadius: 18,
  display: "flex", flexDirection: "column", gap: 6, minHeight: 92,
  boxShadow: "0 4px 14px rgba(15,23,42,0.05)",
});

export default function EmployeeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState("");

  const load = () => {
    api.get(`/payroll/employees/${id}`, { withCredentials: true })
      .then((r) => { setEmp(r.data.employee); setPayslips(r.data.payslips || []); })
      .catch((e) => setError(e.response?.data?.detail || "Empleado no encontrado."));
    api.get(`/payroll/employees/${id}/preview`, { withCredentials: true })
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const set = (k, v) => setEmp({ ...emp, [k]: v });

  const save = async () => {
    setSaving(true); setError(""); setOk("");
    try {
      const payload = {
        first_name: emp.first_name, last_name: emp.last_name, email: emp.email,
        phone: emp.phone, birth_date: emp.birth_date, rfc: emp.rfc, curp: emp.curp,
        nss: emp.nss, position: emp.position, department: emp.department, hire_date: emp.hire_date,
        frequency: emp.frequency, gross_salary: Number(emp.gross_salary || 0),
        bank: emp.bank, clabe: emp.clabe, account_number: emp.account_number,
        status: emp.status === "missing_info" ? "active" : emp.status,
      };
      const r = await api.put(`/payroll/employees/${id}`, payload, { withCredentials: true });
      setEmp(r.data);
      setOk("Guardado.");
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos guardar.");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar a ${emp.display_name}?`)) return;
    try {
      await api.delete(`/payroll/employees/${id}`, { withCredentials: true });
      navigate("/nomina");
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos eliminar.");
    }
  };

  const sendPayslip = async (runId) => {
    setOk(""); setError("");
    if (!emp?.email) {
      setError("Agrega el email del empleado antes de enviarle su recibo.");
      return;
    }
    try {
      const r = await api.post(`/payroll/runs/${runId}/email/${id}`, {}, { withCredentials: true });
      setOk(`Recibo enviado a ${r.data.sent_to}.`);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "resend_not_configured") {
        setError("Aún no está conectado el envío por email. Configura Resend en el backend y podrás enviar recibos.");
      } else {
        setError(typeof detail === "string" ? detail : "No pudimos enviar el recibo.");
      }
    }
  };

  if (!emp) {
    return <AppShell><main className="page" data-testid="employee-detail-page"><p style={{ color: "#6b7280" }}>{error || "Cargando…"}</p></main></AppShell>;
  }

  return (
    <AppShell>
      <main className="page projects-page" data-testid="employee-detail-page">
        <header className="expenses-head">
          <div>
            <button type="button" onClick={() => navigate("/nomina")} className="cash-btn ghost" style={{ marginBottom: 8 }}><ArrowLeft size={14} /> Nómina</button>
            <small className="eyebrow">EMPLEADO</small>
            <h1>{emp.display_name}</h1>
            <p className="academia-subtitle">{emp.position || "Sin puesto"} · {emp.department || "Sin departamento"}</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" onClick={remove} className="cash-btn ghost" data-testid="emp-delete" style={{ color: "#d7565d" }}><Trash2 size={14} /> Eliminar</button>
            <button type="button" onClick={save} disabled={saving} className="cash-btn primary" data-testid="emp-save"><Save size={14} /> {saving ? "Guardando…" : "Guardar cambios"}</button>
          </div>
        </header>

        {error && <p className="expenses-error" data-testid="emp-detail-error">{error}</p>}
        {ok && <p style={{ background: "#dcf7ed", color: "#238a65", padding: 10, borderRadius: 10 }}>{ok}</p>}

        {emp.missing_fields?.length > 0 && (
          <div style={{ background: "#fff2c9", color: "#a97a11", padding: 14, borderRadius: 12, marginBottom: 20 }}>
            Faltan datos: <b>{emp.missing_fields.join(", ")}</b>. Complétalos para poder correr su nómina.
          </div>
        )}

        {preview && (
          <section className="project-kpi-row" data-testid="emp-preview" style={{ marginBottom: 22 }}>
            <div className="kpi-card" style={kpiStyle()}><small>Bruto por periodo</small><b>{money.format(preview.gross || 0)}</b></div>
            <div className="kpi-card" style={kpiStyle("#fff2c9")}><small>ISR</small><b style={{ color: "#a97a11" }}>{money.format(preview.isr || 0)}</b></div>
            <div className="kpi-card" style={kpiStyle("#eef4ff")}><small>IMSS obrero</small><b style={{ color: "#4169e1" }}>{money.format(preview.imss || 0)}</b></div>
            <div className="kpi-card" style={kpiStyle("#dcf7ed")}><small>Neto a pagar</small><b style={{ color: "#238a65" }}>{money.format(preview.net || 0)}</b></div>
          </section>
        )}

        <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={{ background: "#fff", borderRadius: 22, padding: 22, boxShadow: "0 8px 30px rgba(15,23,42,0.06)" }}>
            <h2 style={{ fontSize: 18, marginTop: 0 }}>Datos personales y fiscales</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <label>Nombre(s)<input value={emp.first_name || ""} onChange={(e) => set("first_name", e.target.value)} data-testid="detail-first" /></label>
              <label>Apellidos<input value={emp.last_name || ""} onChange={(e) => set("last_name", e.target.value)} data-testid="detail-last" /></label>
            </div>
            <label>Email<input value={emp.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="detail-email" /></label>
            <label>Teléfono<input value={emp.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="detail-phone" /></label>
            <label>RFC<input value={emp.rfc || ""} onChange={(e) => set("rfc", e.target.value.toUpperCase())} maxLength={13} data-testid="detail-rfc" /></label>
            <label>CURP<input value={emp.curp || ""} onChange={(e) => set("curp", e.target.value.toUpperCase())} maxLength={18} data-testid="detail-curp" /></label>
            <label>NSS<input value={emp.nss || ""} onChange={(e) => set("nss", e.target.value.replace(/\D/g, ""))} maxLength={11} data-testid="detail-nss" /></label>
          </div>

          <div style={{ background: "#fff", borderRadius: 22, padding: 22, boxShadow: "0 8px 30px rgba(15,23,42,0.06)" }}>
            <h2 style={{ fontSize: 18, marginTop: 0 }}>Puesto y pago</h2>
            <label>Puesto<input value={emp.position || ""} onChange={(e) => set("position", e.target.value)} data-testid="detail-position" /></label>
            <label>Departamento<input value={emp.department || ""} onChange={(e) => set("department", e.target.value)} data-testid="detail-department" /></label>
            <label>Fecha de ingreso<input type="date" value={emp.hire_date || ""} onChange={(e) => set("hire_date", e.target.value)} data-testid="detail-hire" /></label>
            <label>Frecuencia
              <select value={emp.frequency || "monthly"} onChange={(e) => set("frequency", e.target.value)} data-testid="detail-frequency">
                {FREQ_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label>Sueldo bruto por periodo<input type="number" step="0.01" value={emp.gross_salary || ""} onChange={(e) => set("gross_salary", e.target.value)} data-testid="detail-salary" /></label>
            <label>Banco<input value={emp.bank || ""} onChange={(e) => set("bank", e.target.value)} data-testid="detail-bank" /></label>
            <label>CLABE<input value={emp.clabe || ""} onChange={(e) => set("clabe", e.target.value.replace(/\D/g, ""))} maxLength={18} data-testid="detail-clabe" /></label>
          </div>
        </section>

        <section data-testid="emp-payslips" style={{ background: "#fff", borderRadius: 22, padding: 22, boxShadow: "0 8px 30px rgba(15,23,42,0.06)", marginTop: 22 }}>
          <h2 style={{ fontSize: 18, marginTop: 0 }}>Historial de recibos</h2>
          {payslips.length === 0 ? (
            <p style={{ color: "#6b7280" }}>Aún no le has corrido nómina a este empleado.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead style={{ background: "#f9fafb" }}>
                <tr>
                  <th style={{ padding: "10px 12px", textAlign: "left" }}>Periodo</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>Bruto</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>ISR</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>IMSS</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", color: "#238a65" }}>Neto</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {payslips.map((p) => (
                  <tr key={p.run_id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "10px 12px" }}>{p.period}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>{money.format(p.gross || 0)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>{money.format(p.isr || 0)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>{money.format(p.imss || 0)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700, color: "#238a65" }}>{money.format(p.net || 0)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                      <a
                        href={`${api.defaults.baseURL}/payroll/runs/${p.run_id}/payslip/${id}.pdf`}
                        target="_blank" rel="noreferrer"
                        className="cash-btn ghost"
                        data-testid={`payslip-pdf-${p.run_id}`}
                        style={{ padding: "6px 10px", fontSize: 12, marginRight: 6 }}
                        title="Descargar recibo PDF"
                      ><FileDown size={13} /></a>
                      <button
                        type="button"
                        onClick={() => sendPayslip(p.run_id)}
                        className="cash-btn ghost"
                        data-testid={`payslip-email-${p.run_id}`}
                        style={{ padding: "6px 10px", fontSize: 12, color: "#4169e1" }}
                        title="Enviar recibo por email"
                      ><Mail size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </main>
    </AppShell>
  );
}
